import { useState } from "react";
import classes from "./FAQSection.module.css";
import { Link } from "react-router-dom";

export const FAQ_SECTION_ID = 'faq';

/** React Router path to the homepage FAQ section. */
export const FAQ_HOME_PATH = { pathname: '/', hash: `#${FAQ_SECTION_ID}` };

const bookerFaqs = [
  {
    q: "Wann bezahle ich?",
    a: (
      <>
        <p>
          Bei deiner Anfrage wird der Betrag sicher über Stripe hinterlegt. Erst nach Bestätigung durch den Anbieter wird die Buchung verbindlich.
        </p>
        <p>
          Wird die Anfrage nicht angenommen oder kommt keine Buchung zustande, erhältst du dein Geld automatisch zurück.
        </p>
      </>
    ),
  },
  {
    q: "Wie kommuniziere ich mit dem Anbieter?",
    a: "Über den integrierten Chat. So bleiben alle Nachrichten und Buchungsdetails an einem Ort.",
  },
];

const providerFaqs = [
  {
    q: "Was kostet die Nutzung?",
    a: (
      <>
        <p>
          Die Registrierung und das Erstellen von Studio-Listings sind kostenlos. Gebühren fallen nur bei erfolgreichen Buchungen an.
        </p>
        <p>
          Aktuell im Soft Launch nehmen wir gar keine Provision. Als Early-Adopter-Vorteil erhalten die ersten 100 Studios auf stu nur 5 % Provision für ein ganzes Jahr.
        </p>
        <p>
          Danach gilt für Anbieter eine Provision von 15 % pro erfolgreicher Buchung.
        </p>
      </>
    ),
  },
  {
    q: "Wie stelle ich mein Studio ein?",
    a: (
      <>
        <p>
          Du kannst dein Studio komplett selbst inserieren und verwalten. Danach kann dein Studio sofort gefunden und gebucht werden.
        </p>
        <p>
          Keine Zeit oder Fragen zur Einrichtung? Wir helfen dir gerne und erstellen dein Studio gemeinsam mit dir.
        </p>
        <p className={classes.answerLinkWrap}>
          <a href="#termin" className={classes.answerLink}>
            Termin vereinbaren.
          </a>
        </p>
      </>
    ),
  },
  {
    q: "Bin ich an einen Vertrag oder eine Laufzeit gebunden?",
    a: (
      <>
        <p>
          Nein. Du gehst keine langfristige Verpflichtung ein. Es gelten lediglich die Nutzungsbedingungen der Plattform.
        </p>
        <p>
          Du kannst dein Studio jederzeit deaktivieren, wieder aktivieren oder dein Konto löschen. Es gibt keine Mindestlaufzeit und keine Exklusivität.
        </p>
      </>
    ),
  },
  {
    q: "Kann ich mein Studio jederzeit wieder offline nehmen?",
    a: "Ja. Du kannst dein Studio jederzeit deaktivieren. Es erscheint dann nicht mehr in der Suche. Bestehende Buchungen und Chats bleiben erhalten.",
  },
  {
    q: "Wann bekomme ich mein Geld?",
    a: "Nach einer erfolgreichen Buchung wird der Betrag automatisch über Stripe auf dein hinterlegtes Konto ausgezahlt.",
  },
];

function ChevronIcon({ open }) {
  return (
    <div className={`${classes.chevron} ${open ? classes.chevronOpen : ""}`}>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M5 7.5L10 12.5L15 7.5"
          stroke="#77808A"
          strokeWidth="1.66667"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }} aria-hidden="true">
      <path d="M3.33333 8H12.6667" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M8 3.33333L12.6667 8L8 12.6667"
        stroke="white"
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AccordionItem({ qa, isOpen, onToggle }) {
  return (
    <div
      className={`${classes.accordionItem} ${isOpen ? classes.accordionItemOpen : ""}`}
      onClick={onToggle}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        className={classes.accordionButton}
      >
        <span className={classes.question}>{qa.q}</span>
        <ChevronIcon open={isOpen} />
      </button>
      <div className={`${classes.accordionPanel} ${isOpen ? classes.accordionPanelOpen : ""}`}>
        <div className={classes.accordionPanelInner}>
          <div className={classes.answer}>{qa.a}</div>
        </div>
      </div>
    </div>
  );
}

function FaqGroup({ title, items, groupKey, openKey, setOpenKey }) {
  return (
    <div className={classes.group}>
      {title && <h3 className={classes.groupTitle}>{title}</h3>}
      <div className={classes.accordionList}>
        {items.map((qa, i) => {
          const key = `${groupKey}-${i}`;
          return (
            <AccordionItem
              key={key}
              qa={qa}
              isOpen={openKey === key}
              onToggle={() => setOpenKey(openKey === key ? null : key)}
            />
          );
        })}
      </div>
    </div>
  );
}

function HeadingBlock({ desktop }) {
  if (desktop) {
    return (
      <div className={classes.headingDesktop}>
        <p className={classes.titleDesktop}>
          Fragen?
          <br aria-hidden="true" />
          Antworten.
        </p>
        <Link to="/kontakt" className={`${classes.cta} ${classes.ctaDesktop}`}>
          Kontaktiere uns
          <ArrowIcon />
        </Link>
      </div>
    );
  }

  return (
    <div className={classes.header}>
      <div className={classes.headingBlock}>
        <p className={classes.title}>FAQ</p>
        <p className={classes.subtitle}>Du hast Fragen? Wir haben Antworten.</p>
      </div>
      <Link to="/kontakt" className={classes.cta}>
        Kontaktiere uns
        <ArrowIcon />
      </Link>
    </div>
  );
}

const FAQSection = () => {
  const [openKey, setOpenKey] = useState(null);

  const groups = (
    <div className={classes.groups}>
      <FaqGroup
        title="Für Buchende"
        items={bookerFaqs}
        groupKey="booker"
        openKey={openKey}
        setOpenKey={setOpenKey}
      />
      <FaqGroup
        title="Für Anbieter"
        items={providerFaqs}
        groupKey="provider"
        openKey={openKey}
        setOpenKey={setOpenKey}
      />
    </div>
  );

  return (
    <section id={FAQ_SECTION_ID} className={classes.section}>
      <div className={classes.container}>
        <div className={classes.headingColumn}>
          <HeadingBlock desktop />
        </div>
        <div className={classes.contentColumn}>
          <HeadingBlock desktop={false} />
          {groups}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
